import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TextField,
  DialogActions,
  DialogContent,
  InputLabel,
  Theme,
  useTheme,
  Stack,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  AppControlledCalendar,
  InputFormat,
} from '../app/appControlledCalendar';
import { AppRow } from '../../layout/appRow';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { AppButton } from '../app/button/appButton';
import {
  NO_PROJECTS,
  NO_TASKS,
  NUMERIC_HALF_INCREMENTS_ONLY,
} from '../../constants';
import { addActivityValidationRegex } from '../../services/validatonService';
import { convertHoursToMinutes, convertMinutesToHours } from '../../services/conversionService';
import { useEmployeeProjectList } from '../../hooks/useEmployeeProjectList';
import { useGetEmployeeTasksQuery } from '../../store/tasks/tasksService';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { Project } from '../../models/project';
import { Task } from '../../models/task';
import {
   formatDateOnly,
} from '../../services/dateService';
import {getLatestActivity} from '../../services/api/activityServices';

const makeStyles = (theme: Theme) => ({
  appRow: {
    mt: 1,
  },

  appRowTwo: {
    mt: 3,
  },

  inputLabel: {
    mb: 3,
  },

  inputLabelSecond: {
    ml: 1,
    [theme.breakpoints.down('sm')]: {
      mr: 2,
    },
  },

  autocomplete: {
    mt: 3,
  },

  textField: {
    mt: 3,
  },

  dialogActions: {
    p: 0,
    mt: 2,
  },

  calendar: {
    width: '170px',
    fontSize: '16px',
  },
  inputTime: {
    width: '170px',
  },
});

export interface ActivityFormData<T = string> {
  id: string;
  project: AutocompleteOption | null;
  task: Task | null;
  date: T;
  hours: string;
  comment: string;
  ticketIdentification: string | null;
  isAMS: boolean;
}

interface Props {
  idEmployee?: string;
  activity: ActivityFormData<string>;
  isLoading?: boolean;
  workingHours: number;
  onSubmit(payload: ActivityFormData<Date>): void;
  onClose(): void;
}

export function ActivityForm({
  idEmployee,
  activity,
  isLoading = false,
  workingHours,
  onSubmit,
  onClose,
}: Props): JSX.Element {
  const theme = useTheme();
  const style = makeStyles(theme);
  const commentsInputRef = useRef<HTMLInputElement | null>(null);

  const methods = useForm({
    defaultValues: { ...activity, date: new Date(formatDateOnly(activity.date)) },
  });

  const {
    watch,
    setValue: setFormValue,
    formState: { errors },
  } = methods;

  const watchedHours = watch('hours', activity.hours);
  const watchTask = watch('task');
  const watchProject = watch('project');
  const isAMS = watch('isAMS', activity.isAMS); 
  const isTaskAMS = useMemo(() => watchTask?.isAMS || false, [watchTask]);

  const [projectsFilterQuery, setProjectsFilterQuery] = useState<string>('');
  const [tasksFilterQuery, setTasksFilterQuery] = useState<string>('');

  const { projects, isLoading: isLoadingProjects } = useEmployeeProjectList(
    idEmployee || '',
    projectsFilterQuery
  );
  const { data: tasksData, isLoading: isLoadingTasks } = useGetEmployeeTasksQuery(
    {
      internalId: idEmployee || '',
      projectId: watchProject?.id || '',
      filter: tasksFilterQuery,
    },
    {
      skip: !idEmployee || !watchProject?.id,
    }
  );
  const tasks = useMemo(() => tasksData?.items || [], [tasksData]);
  const [selectedProject, setSelectedProject] = useState(null as Project | null);

  useEffect(() => {
    activity.date = formatDateOnly(activity.date);
    if (
      activity.project &&
      activity.task &&
      !activity.comment &&
      commentsInputRef.current
    ) {
      commentsInputRef.current.focus();
    }
    if (selectedProject) {
      setFormValue('isAMS', selectedProject.isAMS);
    }
  }, [activity, selectedProject, setFormValue]);

  const totalMinutes = useMemo(
    () => convertHoursToMinutes(Number(watchedHours)),
    [watchedHours]
  );

  const totalWorkingMinutes = 60 * workingHours;

  const isTimeRequired = useMemo(() => !watchedHours, [watchedHours]);

  const onSelectProject = useCallback((justSelectedProject) => {
    if (!idEmployee) {
      return;
    }

    setSelectedProject(justSelectedProject);
    
    setFormValue('isAMS', justSelectedProject?.isAMS || false);
    
    setFormValue('task', null);
    setFormValue('hours', '');
    setFormValue('comment', '');
    setFormValue('ticketIdentification', '');
    setProjectsFilterQuery('');
  }, [idEmployee, setFormValue]);

  const onSearchProjects = useCallback(
    (name: string) => setProjectsFilterQuery(name),
    []
  );

  const onSearchTasks = useCallback(
    (name: string) => setTasksFilterQuery(name),
    []
  );
  const onSelectTask = useCallback(async (selectedTask) => {
    setTasksFilterQuery('');
    
    if (selectedTask && idEmployee) {
      try {
        const latestActivity = await getLatestActivity(selectedTask.id, idEmployee);

        if (latestActivity) {
          setFormValue('comment', latestActivity.comment || '', { shouldDirty: true, shouldTouch: true });
          setFormValue('hours', convertMinutesToHours(latestActivity.minutes).toString(), { shouldDirty: true, shouldTouch: true });
          setFormValue('ticketIdentification', latestActivity.ticketIdentification || '', { shouldDirty: true, shouldTouch: true });
        }
      } catch (error) {
        console.warn('Could not fetch latest activity:', error);
      }
    }
  }, [idEmployee, setFormValue]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        autoComplete="off"
      >
        <DialogContent>
          <AppRow sx={style.appRow}>
            <InputLabel variant="filled" htmlFor="date" sx={style.inputLabel}>
              Date
            </InputLabel>
            <AppControlledCalendar
              sx={style.calendar}
              name="date"
              inputFormat={InputFormat.SEPARATOR_SPACE}
            />
          </AppRow>
          <AppControlledAutocomplete
            name="project"
            label="Project"
            options={projects}
            searchOptions={onSearchProjects}
            onSelect={onSelectProject}
            sx={style.autocomplete}
            isError={!!errors.project}
            isLoading={isLoadingProjects}
            noOptionsText={NO_PROJECTS}
            isRequired
          />

          <AppControlledAutocomplete
            name="task"
            label="Task"
            options={tasks}
            getOptionLabel={(task) => (typeof task === 'string' ? task : task.name)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={style.autocomplete}
            isError={!!errors.task}
            disabled={!watchProject}
            isLoading={isLoadingTasks}
            noOptionsText={NO_TASKS}
            isRequired
            searchOptions={onSearchTasks}
            onSelect={onSelectTask}
          />

          {(isAMS || isTaskAMS) && (
          <AppRow sx={style.appRowTwo} >
            <InputLabel
              sx={style.inputLabelSecond}
              htmlFor="ticketIdentificationLabel"
              error={!!errors.ticketIdentification}
            >
              Ticket #
            </InputLabel>
            <Stack flexDirection={'row'} gap={2}>
              
              <AppControlledTextField
                name="ticketIdentification"
                label="Insert your ticket #"
                placeholder="Ticket # here"
                isRequired={isAMS || isTaskAMS}
                isError={!!errors.ticketIdentification}
                InputProps={{ style: { textAlign: 'center' } }}
              />
            </Stack>
          </AppRow>
          )}

          <AppRow sx={style.appRowTwo}>
            <InputLabel
              sx={style.inputLabelSecond}
              htmlFor="hours"
              error={!!errors.hours}
            >
              Logged (h)
            </InputLabel>

            <Stack flexDirection={'row'} gap={2}>
              <AppControlledTextField
                name="hours"
                label="Hours"
                placeholder="Hours"
                isRequired={isTimeRequired}
                minValue={0}
                maxValue={8}
                isError={!!errors.hours}
                rules={{
                  validate: {
                    validHours: () => {
                      return (
                        totalMinutes <= totalWorkingMinutes ||
                        `The time can't be more than ${workingHours} hours`
                      );
                    },
                  },
                }}
                sx={style.inputTime}
                pattern={{
                  value: addActivityValidationRegex,
                  message: NUMERIC_HALF_INCREMENTS_ONLY,
                }}
                InputProps={{ style: { textAlign: 'center' } }}
              />
            </Stack>
          </AppRow>
          <TextField
            {...methods.register('comment')}
            multiline
            label="Comment"
            sx={style.textField}
            fullWidth
            rows={4}
            inputRef={commentsInputRef}
            InputLabelProps={{
              shrink: !!watch('comment') || undefined, // Force shrink when field has value
            }}
          />
        </DialogContent>
        <DialogActions sx={style.dialogActions}>
          <AppButton label="Cancel" onClick={onClose} />
          <AppButton
            label="Save"
            isPrimary
            buttonType="submit"
            isLoading={isLoading}
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
}
